// Settings → General → Email Settings: morning lien watch digest to accounting (on/off, recipients, send now).
import { useEffect, useRef, useState } from "react";
import { tr } from "@/i18n";
import { toast } from "sonner";
import { Gavel } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { getLienDigestSettings, updateLienDigestSettings, sendLienDigestNow, runLienDailyNow } from "@/lib/api";

const errMsg = (e) => e?.response?.data?.detail || e?.message || "Failed";

export function LienDigestSettings() {
  const [cfg, setCfg] = useState(null);
  const [recips, setRecips] = useState("");
  const [busy, setBusy] = useState("");
  const dirty = useRef(false); // never overwrite what the user is typing with a late GET

  const load = async () => {
    try {
      const { data } = await getLienDigestSettings();
      setCfg(data);
      if (!dirty.current) setRecips((data.recipients || []).join(", "));
    } catch { /* panel just stays collapsed */ }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy("save");
    try {
      const { data } = await updateLienDigestSettings({
        enabled: patch.enabled ?? cfg.enabled,
        recipients: (patch.recipients ?? recips).split(",").map((s) => s.trim()).filter(Boolean),
      });
      dirty.current = false;
      setCfg(data); setRecips((data.recipients || []).join(", "));
      toast.success(data.enabled ? "Lien watch digest is ON" : "Lien watch digest saved");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };

  const sendNow = async () => {
    const goesTo = cfg.effective_recipients || [];
    if (!goesTo.length) { toast.error("Nobody to send to — add a recipient or give an Accounting user an email"); return; }
    if (!(cfg.recipients || []).length && !window.confirm(`No recipients are set, so the digest goes to everyone with the Accounting role:\n${goesTo.join(", ")}\n\nSend it now?`)) return;
    setBusy("send");
    try {
      const { data } = await sendLienDigestNow();
      if (data.status === "sent") toast.success(`Digest sent to ${(data.to || []).join(", ")} — ${data.new} new, ${data.on_watch} on watch`);
      else toast.warning(`Digest not sent: ${data.reason || data.error || data.status}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };

  const runNow = async () => {
    setBusy("run");
    try {
      const { data } = await runLienDailyNow();
      const a = data.auto_reminders || {};
      toast.success(`Daily AR run done — ${(a.sent || []).length} auto notice${(a.sent || []).length === 1 ? "" : "s"} sent, digest ${data.digest?.status || "—"}${data.digest?.reason ? ` (${data.digest.reason.replace("_", " ")})` : ""}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };

  if (!cfg) return null;
  const last = cfg.last_run;
  return (
    <div className="border rounded-lg p-3 space-y-3 border-amber-200 bg-amber-50/40" data-testid="lien-digest-settings">
      <div className="flex items-start gap-3">
        <Gavel className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" />
        <div className="flex-1">
          <div className="text-xs font-semibold text-slate-700">Lien watch digest — every morning at 7:00 (Denver)</div>
          <div className="text-[11px] text-slate-500 leading-snug">
            Emails accounting every invoice that <strong>entered</strong> the 30-day lien watch since the last digest, with the rest
            of the watch list below. Nothing new → no email. The same 7 AM run also sends any per-invoice <strong>auto lien notices</strong>
            (the bell under Lien Actions on the AR view).
          </div>
        </div>
        <Switch checked={!!cfg.enabled} disabled={busy === "save"} onCheckedChange={(v) => save({ enabled: v })} data-testid="lien-digest-switch" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Recipients</Label>
        <div className="flex gap-2">
          <Input value={recips} onChange={(e) => { dirty.current = true; setRecips(e.target.value); }} placeholder="Blank = everyone with the Accounting role"
            className="h-8 text-xs flex-1" data-testid="lien-digest-recipients" />
          <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy === "save"} onClick={() => save({})} data-testid="lien-digest-save">{tr("Save")}</Button>
        </div>
        <div className="text-[10px] text-slate-400">
          Goes to: {(cfg.effective_recipients || []).length ? cfg.effective_recipients.join(", ") : <span className="text-rose-600">nobody — no Accounting user has an email; add recipients above</span>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!!busy} onClick={sendNow} data-testid="lien-digest-send-now">
          {busy === "send" ? "Sending…" : "Send digest now"}
        </Button>
        <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!!busy} onClick={runNow} data-testid="lien-daily-run-now"
          title="Runs the whole 7 AM job now: due auto notices + the digest">
          {busy === "run" ? "Running…" : "Run daily AR job now"}
        </Button>
        {last && (
          <span className="text-[10px] text-slate-500 num ml-auto" data-testid="lien-daily-last-run">
            Last run {new Date(last.at).toLocaleString([], { month: "numeric", day: "numeric", year: "2-digit", hour: "numeric", minute: "2-digit" })} ({last.trigger}) · {last.auto_sent} auto notice{last.auto_sent === 1 ? "" : "s"}{last.auto_errors ? <span className="text-rose-600"> · {last.auto_errors} could not send</span> : null} · digest {last.digest_status}{last.digest_reason ? ` (${String(last.digest_reason).replace("_", " ")})` : ""}
          </span>
        )}
      </div>
    </div>
  );
}
